import React, {FC} from "react";
import {Icon, Popup, Table} from "semantic-ui-react";
import {DataType} from "@/util/dataFormatUtil";
import {
    IColumnMetadata,
    IDataTableData
} from "../../features/application/api/applicationModels";
import {sortingDirections} from "../../util/sortUtil";
import DataTableHeaderFilterString from "./DataTableHeaderFilterString";
import DataTableHeaderFilterNumerical from "./DataTableHeaderFilterNumerical";
import DataTableHeaderFilterBoolean from "./DataTableHeaderFilterBoolean";
import {
    IDataTableFilter,
    IDataTableFilterMap,
    INumericalFilter
} from "./DataTableModels";
import {ComparisonMethodType} from "@/features/monitors/api/monitorsModels";

interface IDataTableHeaderProps {
    data?: IDataTableData[];
    staticColumns: IColumnMetadata[];
    sortColumnKey: string;
    sortDirection: sortingDirections;
    configurableColumns?: IColumnMetadata[];
    filters?: Map<string, IDataTableFilter>;
    handleSort: (clickedColumn: string) => void;
    setFilters?: (
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ) => void;
}

const DataTableHeader: FC<IDataTableHeaderProps> = ({
    data,
    staticColumns,
    sortColumnKey,
    sortDirection,
    configurableColumns,
    filters,
    handleSort,
    setFilters
}) => {
    return (
        <Table.Header>
            <Table.Row>
                {staticColumns.map((column) => (
                    <Table.HeaderCell
                        key={column.key}
                        sorted={
                            sortColumnKey === column.key ? sortDirection : undefined
                        }
                        onClick={() => handleSort(column.key)}
                    >
                        {column.hasFilter === true &&
                            data &&
                            setFilters !== undefined &&
                            displayFilter(column, data, setFilters, filters)}

                        {column.label}
                        {displayTooltip(column.tooltipText)}
                    </Table.HeaderCell>
                ))}
                {configurableColumns?.map((column) => (
                    <Table.HeaderCell
                        key={column.key}
                        sorted={
                            sortColumnKey === column.key ? sortDirection : undefined
                        }
                        onClick={() => handleSort(column.key)}
                    >
                        {column.hasFilter === true &&
                            data &&
                            setFilters !== undefined &&
                            displayFilter(
                                {
                                    key: column.key,
                                    label: column.label,
                                    dataType: column.dataType,
                                    decimalPlaces: column.decimalPlaces,
                                    hasFilter: column.hasFilter,
                                    unit: column.unit
                                },
                                data,
                                setFilters,
                                filters
                            )}

                        {column.label}
                        {displayTooltip(column.tooltipText)}
                    </Table.HeaderCell>
                ))}
            </Table.Row>
        </Table.Header>
    );
};

function displayFilter(
    column: IColumnMetadata,
    data: IDataTableData[] | undefined,
    setFilters: (
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ) => void,
    filters: IDataTableFilterMap | undefined
): React.ReactNode {
    if (column.hasFilter === true && data) {
        if (
            column.dataType === DataType.String ||
            column.dataType === DataType.Date ||
            column.dataType === DataType.DateTime ||
            column.dataType === DataType.None
        ) {
            return (
                <>
                    <DataTableHeaderFilterString
                        filterOptions={getFilterOptions(data, column)}
                        column={column}
                        setFilters={setFilters}
                        appliedFilters={
                            filters?.has(column.key)
                                ? filters.get(column.key)
                                : undefined
                        }
                    ></DataTableHeaderFilterString>
                    <br />
                </>
            );
        } else if (column.dataType === DataType.Money) {
            return (
                <>
                    <DataTableHeaderFilterNumerical
                        column={column}
                        setFilters={setFilters}
                        appliedFilter={
                            //appliedFilter can only be a single INumericalFilter so just get the first one from the Set
                            filters?.has(column.key)
                                ? (filters.get(column.key)?.values().next()
                                      .value as INumericalFilter) || undefined
                                : undefined
                        }
                        labelPrefix={"$"}
                        comparisonOptions={[
                            {
                                key: 0,
                                text: "≥",
                                value: ComparisonMethodType.GreaterThanOrEqual
                            },
                            {
                                key: 1,
                                text: "≤",
                                value: ComparisonMethodType.LessThanOrEqual
                            },
                            {
                                key: 2,
                                text: "=",
                                value: ComparisonMethodType.Equal
                            },
                            {
                                key: 3,
                                text: "≠",
                                value: ComparisonMethodType.NotEqual
                            }
                        ]}
                    ></DataTableHeaderFilterNumerical>
                    <br />
                </>
            );
        } else if (column.dataType === DataType.Number) {
            return (
                <>
                    <DataTableHeaderFilterNumerical
                        column={column}
                        setFilters={setFilters}
                        appliedFilter={
                            //appliedFilter can only be a single INumericalFilter so just get the first one from the Set
                            filters?.has(column.key)
                                ? (filters.get(column.key)?.values().next()
                                      .value as INumericalFilter)
                                : undefined
                        }
                        comparisonOptions={[
                            {
                                key: 0,
                                text: "≥",
                                value: ComparisonMethodType.GreaterThanOrEqual
                            },
                            {
                                key: 1,
                                text: "≤",
                                value: ComparisonMethodType.LessThanOrEqual
                            },
                            {
                                key: 2,
                                text: "=",
                                value: ComparisonMethodType.Equal
                            },
                            {
                                key: 3,
                                text: "≠",
                                value: ComparisonMethodType.NotEqual
                            }
                        ]}
                    ></DataTableHeaderFilterNumerical>
                    <br />
                </>
            );
        } else if (
            column.dataType === DataType.TimeSpan ||
            column.dataType === DataType.PrettyTimeSpan
        ) {
            return (
                <>
                    <DataTableHeaderFilterNumerical
                        column={column}
                        setFilters={setFilters}
                        appliedFilter={
                            //appliedFilter can only be a single INumericalFilter so just get the first one from the Set
                            filters?.has(column.key)
                                ? (filters.get(column.key)?.values().next()
                                      .value as INumericalFilter) || undefined
                                : undefined
                        }
                        comparisonOptions={[
                            {
                                key: 0,
                                text: "≥",
                                value: ComparisonMethodType.GreaterThanOrEqual
                            },
                            {
                                key: 1,
                                text: "≤",
                                value: ComparisonMethodType.LessThanOrEqual
                            }
                        ]}
                    ></DataTableHeaderFilterNumerical>
                    <br />
                </>
            );
        } else if (
            column.dataType === DataType.TimeOfDay ||
            column.dataType === DataType.Time
            // Even though DataType.Time comes in as an ISO string, it is necessary to run it through the numerical filter,
            // since user input time does not have an associated date, and thus cannot be compared to the raw ISO string.
            // It is easier to treat Time as a TimeOfDay (i.e. minutes after midnight), and adding extra logic to the filtering
            // in index.tsx to accomodate this.
        ) {
            return (
                <>
                    <DataTableHeaderFilterNumerical
                        column={column}
                        setFilters={setFilters}
                        appliedFilter={
                            //appliedFilter can only be a single INumericalFilter so just get the first one from the Set
                            filters?.has(column.key)
                                ? (filters.get(column.key)?.values().next()
                                      .value as INumericalFilter) || undefined
                                : undefined
                        }
                        comparisonOptions={[
                            {
                                key: 0,
                                text: "After",
                                value: ComparisonMethodType.GreaterThanOrEqual
                            },
                            {
                                key: 1,
                                text: "Before",
                                value: ComparisonMethodType.LessThanOrEqual
                            },
                            {
                                key: 2,
                                text: "Between",
                                value: ComparisonMethodType.Between
                            }
                        ]}
                    ></DataTableHeaderFilterNumerical>
                    <br />
                </>
            );
        } else if (column.dataType === DataType.Percent) {
            return (
                <>
                    <DataTableHeaderFilterNumerical
                        column={column}
                        setFilters={setFilters}
                        appliedFilter={
                            //appliedFilter can only be a single INumericalFilter so just get the first one from the Set
                            filters?.has(column.key)
                                ? (filters.get(column.key)?.values().next()
                                      .value as INumericalFilter) || undefined
                                : undefined
                        }
                        labelSuffix={"%"}
                        comparisonOptions={[
                            {
                                key: 0,
                                text: "≥",
                                value: ComparisonMethodType.GreaterThanOrEqual
                            },
                            {
                                key: 1,
                                text: "≤",
                                value: ComparisonMethodType.LessThanOrEqual
                            },
                            {
                                key: 2,
                                text: "=",
                                value: ComparisonMethodType.Equal
                            },
                            {
                                key: 3,
                                text: "≠",
                                value: ComparisonMethodType.NotEqual
                            }
                        ]}
                    ></DataTableHeaderFilterNumerical>
                    <br />
                </>
            );
        } else if (column.dataType === DataType.Boolean) {
            return (
                <>
                    <DataTableHeaderFilterBoolean
                        column={column}
                        setFilters={setFilters}
                        appliedFilters={
                            filters?.has(column.key)
                                ? filters.get(column.key)
                                : undefined
                        }
                    ></DataTableHeaderFilterBoolean>
                    <br />
                </>
            );
        }
    }
}

function displayTooltip(tooltip: string | undefined): React.ReactNode {
    if (!tooltip || tooltip.trim() === "") {
        return null;
    }

    return (
        <>
            <br />
            <Popup
                trigger={<Icon name="help circle" color="grey" />}
                content={<div style={{whiteSpace: "pre-wrap"}}>{tooltip}</div>}
                position="bottom center"
            />
        </>
    );
}

//returns the unique set of values from a column of data from the IDataTableData[]
function getFilterOptions(
    data: IDataTableData[],
    column: IColumnMetadata
): Set<string | number | boolean | null | undefined> {
    const options = new Set(
        data.map((item: IDataTableData) => item[column.key]).sort()
    );

    return options;
}

export default DataTableHeader;
